import { useScreenSize } from "@booking/hooks/useScreenSize";
import { LabelResource } from "@search/api/types";
import FilterPills from "@search/components/FilterPills";
import { SortFilter } from "@search/components/SortFilter";
import {
  ResultCard,
  type ResultCardProps,
} from "@search/components/constructs/ResultCard";
import { type Filters } from "@search/store/types";
import clsx from "clsx";
import pluralize from "pluralize";
import React from "react";

import MoreResults from "@/components/compounds/bumpers/MoreResults";
import { ColorEnum } from "@/enums";
import { type CommonProps } from "@/types";
import { createId } from "@/utils/helpers";

import styles from "./ResultList.module.css";

const ref = "result-list";

interface ResultListProps extends Partial<CommonProps> {
  filters: Filters;
  limitPerPage: number;
  nameResources: LabelResource;
  results: Array<ResultCardProps>;
  suggestions: Array<ResultCardProps>;
  totalResults: number;
}

function displayPageRange(
  page: number,
  limitPerPage: number,
  resultCount: number,
) {
  if (page === 1) {
    return;
  }

  const start = (page - 1) * limitPerPage + 1;
  const end = start + resultCount - 1;

  return (
    <small>
      (Showing {start}&hellip;{end})
    </small>
  );
}

function useResultList(props: ResultListProps) {
  const className = clsx(styles[ref], props.className);
  const { id = createId(ref) } = props;

  return {
    className,
    id,
  };
}

function ResultList(props: ResultListProps) {
  const { className, id } = useResultList(props);
  const {
    filters,
    limitPerPage,
    nameResources,
    results,
    suggestions,
    totalResults,
  } = props;
  const { isMinLg } = useScreenSize();
  const h2Id = `${id}_title`;

  const pageRange = displayPageRange(
    filters.page,
    limitPerPage,
    results.length,
  );

  const maxDistance =
    Math.ceil(
      Math.max.apply(
        null,
        suggestions.map(({ details }) => details.distance ?? 0),
      ) / 10,
    ) * 10;

  return (
    <section className={className} id={id} aria-labelledby={h2Id}>
      <header>
        <h2 id={h2Id}>
          {`We found ${pluralize("great golf trip", totalResults, true)}`}
          {pageRange}
        </h2>

        {isMinLg && totalResults > 0 && <SortFilter filters={filters} />}
      </header>

      <FilterPills filters={filters} nameResources={nameResources} />

      {results.map((result, index) => (
        <ResultCard
          key={`result_${index}`}
          {...result}
          flightsFilter={filters.flights}
        />
      ))}

      {suggestions.length > 0 && (
        <React.Fragment>
          <MoreResults
            color={ColorEnum.GREEN}
            count={suggestions.length}
            distance={maxDistance}
          />

          {suggestions.map((result, index) => (
            <ResultCard
              key={`result2_${index}`}
              {...result}
              flightsFilter={filters.flights}
            />
          ))}
        </React.Fragment>
      )}
    </section>
  );
}

export default ResultList;
export { ResultList, type ResultListProps };
